import { useState, useCallback, useEffect } from 'react';
import moment from 'moment';
import { Box, Button, Chip, IconButton, Paper, Stack } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useParams } from 'react-router';
import EditArticleModal from './components/edit-article-modal';
import { useStringsContext } from '../../../contexts/strings';
import { useArticles } from './hooks/useArticles';
import { App, Article, ArticleStatus, Author } from './types';
import { ROLES, SxStyle } from '../../../types';
import { getArticleStatus } from './utils/status';
import PreviewArticleModal from './components/preview-article-modal';
import EditIcon from '@mui/icons-material/Edit';
import PreviewIcon from '@mui/icons-material/Visibility';
import { useUserAuth } from '../../../contexts/auth';
import ArticlesPushNotificationColumn from './components/article-push-notification-column';

const styles: SxStyle = {
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: 4,
  },
  container: (theme) => ({
    width: '100%',
    height: 'calc(100% - 60px)',
    marginTop: theme.spacing(4),
  }),
  dataGrid: {
    '.MuiDataGrid-root .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '.MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none',
    },
    '.MuiDataGrid-columnHeaderTitleContainer, .MuiDataGrid-columnHeaderTitleContainerContent': {
      width: '100%',
    },
    '.MuiDataGrid-row:nth-of-type(odd)': {
      backgroundColor: 'shades.p10',
    },
  },
};

interface ListEditButtonsProps {
  params: GridRenderCellParams;
  onEdit?: (article: Article) => void;
  onPreview?: (article: Article) => void;
}

const ListEditButtons = ({ params, onEdit, onPreview }: ListEditButtonsProps) => (
  <Stack spacing={1} direction="row" alignItems="center">
    <IconButton onClick={() => onEdit?.(params.row)} disabled={!onEdit} color="primary">
      <EditIcon />
    </IconButton>
    <IconButton onClick={() => onPreview?.(params.row)} disabled={!onPreview} color="primary">
      <PreviewIcon />
    </IconButton>
  </Stack>
);

function ArticleStatusChip({ status }: { status: ArticleStatus }) {
  const { strings } = useStringsContext();
  let color: 'default' | 'warning' | 'success' | 'error' = 'default';
  switch (status) {
    case 'scheduled':
      color = 'warning';
      break;
    case 'published':
      color = 'success';
      break;
    case 'archived':
      color = 'error';
      break;
    default:
  }

  return <Chip label={strings.articles[status]} size="small" color={color} />;
}

function ArticlesList() {
  const params = useParams<{ abbrv: string }>();
  const selectedAbbrv = params.abbrv;
  const { strings } = useStringsContext();
  const [loading, setLoading] = useState(true);
  const { fetchArticles } = useArticles();
  const [articles, setArticles] = useState<Article[]>();
  const [editArticle, setEditArticle] = useState<Article | null>(null);
  const [previewArticle, setPreviewArticle] = useState<Article | null>(null);

  const { isSuperAdmin, teamPermissions, hasTeamRole } = useUserAuth();

  const canEditArticle = (article: Article) =>
    isSuperAdmin || article.apps.every((app) => teamPermissions[app.abbrv] === 'admin');

  const columns: GridColDef[] = [
    {
      field: 'createdAt',
      valueGetter: (value, row) => {
        return moment(row.createdAt).format('YYYY-MM-DD HH:mm');
      },
      headerName: strings.articles.createdAt,
      width: 140,
    },
    {
      field: 'updatedAt',
      valueGetter: (value, row) => {
        return moment(row.updatedAt).format('YYYY-MM-DD HH:mm');
      },
      headerName: strings.articles.updatedAt,
      width: 140,
    },
    { field: 'title', headerName: strings.articles.fieldNameTitle, flex: 1 },
    { field: 'description', headerName: strings.articles.fieldNameDescription, flex: 1 },
    {
      field: 'apps',
      headerName: strings.articles.fieldNameAppAbbrvs,
      renderCell: (params) => (
        <Stack direction="row" gap={1} alignItems="center">
          {(params.value as App[]).map((t) => (
            <Chip key={t.id} label={t.abbrv} size="small" />
          ))}
        </Stack>
      ),
      width: 140,
    },
    { field: 'excerpt', headerName: strings.articles.fieldNameExcerpt },
    {
      field: 'authors',
      headerName: strings.articles.fieldNameAuthors,
      valueGetter: (value, row) => row.authors.map((author: Author) => author.name).join(', '),
    },
    {
      field: 'status',
      headerName: strings.articles.status,
      renderCell: (params: GridRenderCellParams) => (
        <div>
          <ArticleStatusChip status={getArticleStatus(params.row)} />
        </div>
      ),
      width: 120,
      align: 'center',
    },
    {
      field: 'push-notification',
      headerName: strings.articles.pushNotificationColumn,
      minWidth: 270,
      renderCell: (params: GridRenderCellParams) => (
        <>
          {params.row.apps[0] ? (
            <ArticlesPushNotificationColumn
              key={params.row.id}
              article={params.row}
              selectedAbbrv={selectedAbbrv}
            />
          ) : null}
        </>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      minWidth: 100,
      maxWidth: 100,
      align: 'right',
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <ListEditButtons
          params={params}
          onEdit={canEditArticle(params.row) ? (article) => setEditArticle(article) : undefined}
          onPreview={(article) => setPreviewArticle(article)}
        />
      ),
    },
  ];

  const getArticles = useCallback(
    async (previewArticleId?: string) => {
      setLoading(true);
      try {
        const results = await fetchArticles(selectedAbbrv);
        setArticles(results);
        if (previewArticleId) {
          setEditArticle(null);
          setPreviewArticle(results.find((article) => article.id === previewArticleId) || null);
        }
      } catch (error) {
        console.error(error);
      }
      setLoading(false);
    },
    [fetchArticles, selectedAbbrv]
  );

  useEffect(() => {
    getArticles();
  }, [getArticles]);

  const createArticle = () => {
    setEditArticle({} as Article);
  };

  const isReadOnly = !isSuperAdmin && hasTeamRole(selectedAbbrv, ROLES.VIEWER);

  return (
    <>
      <Box sx={styles.actions}>
        <Button variant="contained" color="primary" onClick={createArticle} disabled={isReadOnly}>
          {strings.articles.createArticle}
        </Button>
      </Box>
      <Paper elevation={4} sx={styles.container}>
        <DataGrid
          loading={loading}
          sx={styles.dataGrid}
          rows={articles || []}
          columns={columns}
          autoPageSize
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          initialState={{
            sorting: {
              sortModel: [
                {
                  field: 'createdAt',
                  sort: 'desc',
                },
              ],
            },
          }}
        />
        {editArticle && (
          <EditArticleModal
            onDone={() => {
              setEditArticle(null);
              getArticles();
            }}
            onPreview={getArticles}
            article={editArticle}
            open={!!editArticle}
            onClose={() => {
              setEditArticle(null);
            }}
          />
        )}
        {previewArticle && (
          <PreviewArticleModal
            onDone={() => {
              setPreviewArticle(null);
              getArticles();
            }}
            onEdit={() => {
              setEditArticle(previewArticle);
              setPreviewArticle(null);
            }}
            article={previewArticle}
            open={!!previewArticle}
            onClose={() => setPreviewArticle(null)}
            isReadOnly={isReadOnly}
          />
        )}
      </Paper>
    </>
  );
}

export default ArticlesList;
